import React, { useState, useMemo } from 'react';

import { Button, CircularProgress, TextField, Box, Typography } from "@mui/material";
import ButtonGroup from "@mui/material/ButtonGroup";
import { useMutation } from "@apollo/client";
import { SEND_EMAIL_TO_SUPPORT, UPDATE_CATERER } from "../../Graphql/mutations";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {toggleSnackbar} from "../../ReactiveVariables/index";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {useNavigate} from "react-router-dom";
import {surveyYupSchema} from "./configurations";

export default function Survey(props) {
    const navigate = useNavigate();
    const userType = props?.userType?.user;
    const userData = props?.user?.data?.createUserIdentity || props?.userValues?.updateCaterer;

    const searchParams = new URLSearchParams(location.search);
    const redirectPath = searchParams.get("redirect") || location.state?.redirect || "/";

    const [selectedOption, setSelectedOption] = useState('');
    const [referralDetail, setReferralDetail] = useState('');

    const marketingOptions = useMemo(() => {
        const options = [
            { key: 'referral', label: 'Referral', icon: '👥' },
            { key: 'linkedIn', label: 'LinkedIn', icon: '💼' },
            { key: 'googleAds', label: 'Google Ads', icon: '📢' },
            { key: 'googleSearch', label: 'Google Search', icon: '🔍' },
            { key: 'instagram', label: 'Instagram', icon: '📷' },
            { key: 'tiktok', label: 'TikTok', icon: '🎵' },
            { key: 'colleenJohnson', label: 'Colleen Johnson', icon: '👤' },
        ];
        
        // Shuffle all options except "Other"
        const shuffledOptions = options.sort(() => 0.5 - Math.random());
        
        // Always add "Other" at the end
        return [...shuffledOptions, { key: 'other', label: 'Other', icon: '💭' }];
    }, []);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({ resolver: yupResolver(surveyYupSchema), mode: 'onChange' });

    const [sendEmailToSupport, {loading: sendEmailLoading}] = useMutation(SEND_EMAIL_TO_SUPPORT);

    const [updateCaterer, {loading: updateCatererLoading}] = useMutation(UPDATE_CATERER, {
        onCompleted: (res) => {
            props.setUserValues(res);
            if (userType === 'Customer') {
                props.setActiveStep(1)
            }
            props.setActiveStep(6);
        },
    });

    const isOptionSelected = !!selectedOption;

    function onSubmit(value) {
        try {
            // Updated answerOptions and optionKeyMap to match backend schema
            // Include every option the backend `optionVariables` input requires
            // (all Boolean!), so the mutation validates. chrisSales/jeffSales are
            // not selectable in this UI but must still be sent as false.
            const answerOptions = {
              referral: false,
              linkedIn: false,
              googleAds: false,
              googleSearch: false,
              instagram: false,
              tiktok: false,
              chrisSales: false,
              jeffSales: false,
              colleenJohnson: false,
              other: false
            };

            const optionKeyMap = {
              referral: 'referral',
              linkedIn: 'linkedIn',
              googleAds: 'googleAds',
              googleSearch: 'googleSearch',
              instagram: 'instagram',
              tiktok: 'tiktok',
              colleenJohnson: 'colleenJohnson',
              other: 'other'
            };

            const mappedKey = optionKeyMap[selectedOption];
            if (mappedKey) {
              answerOptions[mappedKey] = true;
            }

            value.answerOptions = answerOptions;
            if (selectedOption === 'referral') {
                value.referralDetail = referralDetail;
            }
            if (selectedOption !== 'other') {
                value.answer = '';
            }
            let finalValue = { ...value };
            if (props?.userType?.user === "Caterer") {
                const surveyData = {
                    tabValue: "5",
                }
                Promise.all([
                    updateCaterer({
                        variables: {
                            id: props?.catererId,
                            data: surveyData
                        }
                    }),
                    sendEmailToSupport({ variables: { data: { ...finalValue } } })
                ]).then((res) => {
                    if (!sendEmailLoading) {
                        toggleSnackbar({
                            status: true,
                            message: "The survey has been successfully sent",
                            variant: 'success'
                        });
                        navigate(redirectPath);
                    }
                }).catch((err) => {
                    const message = err && err.message;
                    toggleSnackbar({
                        status: true,
                        message: message,
                        variant: "error",
                    });
                });
            } else {
                sendEmailToSupport({ variables: { data: { ...finalValue } } })
                    .then((res) => {
                        if (!sendEmailLoading) {
                            toggleSnackbar({
                                status: true,
                                message: "The survey has been successfully sent",
                                variant: 'success'
                            });
                            navigate("/");
                        }
                    }).catch((err) => {
                        const message = err && err.message;
                        toggleSnackbar({
                            status: true,
                            message: message,
                            variant: "error",
                        });
                    });
            }

        } catch (e) {
            const message = e?.message || 'An error occurred';
            toggleSnackbar({
                status: true,
                message: message,
                variant: "error",
            });
        }
    }

    function skip() {
        toggleSnackbar({
            status: true,
            message: "Survey Info skipped successfully",
            variant: "success",
        });
        navigate(redirectPath);
    }

    return (
        <div className="create-step-form">
            {/* Improved Header */}
            <Box 
                sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    p: 3,
                    mb: 3,
                    borderRadius: 2,
                    textAlign: 'center'
                }}
            >
                <Typography variant="h5" component="h5" className="h5" sx={{ 
                    color: 'white', 
                    fontWeight: 600, 
                    mb: 1,
                    fontSize: { xs: '1.25rem', md: '1.5rem' }
                }}>
                    How did you hear about us?
                </Typography>
                <Typography variant="subtitle1" className="subhead" sx={{ 
                    color: 'rgba(255,255,255,0.9)', 
                    fontSize: '0.9rem' 
                }}>
                    Select the option that best describes how you discovered our service
                </Typography>
            </Box>

            <div className="row addBusinessSet">
                <div>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className='create-step-form'>
                            <div className='row'>
                                {/* Hidden form fields - keeping as is */}
                                <div className='col-md-6 textBox d-none'>
                                    <TextField
                                        label="FIRST NAME*"
                                        autoFocus
                                        variant="outlined"
                                        className="textField allFeild"
                                        defaultValue={userData?.firstName || ''}
                                        {...register('firstname')}
                                    />
                                    {errors.firstname && (
                                        <span className="error-msg">{errors.firstname.message}</span>
                                    )}
                                </div>
                                <div className='col-md-6 textBox d-none'>
                                    <TextField
                                        label="LAST NAME*"
                                        autoFocus
                                        variant="outlined"
                                        className="textField allFeild"
                                        defaultValue={userData?.lastName || ''}
                                        {...register('lastname')}
                                    />
                                    {errors.lastname && (
                                        <span className="error-msg">{errors.lastname.message}</span>
                                    )}
                                </div>
                                <div className='textBox d-none'>
                                    <TextField
                                        label="EMAIL*"
                                        autoFocus
                                        variant="outlined"
                                        className="textField allFeild"
                                        defaultValue={userData?.email || ''}
                                        fullWidth
                                        {...register('email')}
                                    />
                                    {errors.email && (
                                        <span className="error-msg">{errors.email.message}</span>
                                    )}
                                </div>
                                
                                {/* Improved ButtonGroup */}
                                <div>
                                    <ButtonGroup
                                      orientation="vertical"
                                      variant="outlined"
                                      sx={{ 
                                        gap: 1.5, 
                                        width: '100%', 
                                        '& .MuiButton-root': {
                                          border: '2px solid #e2e8f0',
                                          borderRadius: '12px !important',
                                          p: 2,
                                          textAlign: 'left',
                                          justifyContent: 'flex-start',
                                          fontSize: '0.95rem',
                                          fontWeight: 500,
                                          textTransform: 'none',
                                          transition: 'all 0.2s ease-in-out',
                                          '&:hover': {
                                            borderColor: '#667eea',
                                            backgroundColor: 'rgba(102, 126, 234, 0.05)',
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                          }
                                        }
                                      }}
                                    >
                                      {marketingOptions.map(option => (
                                        <Button
                                          key={option.key}
                                          onClick={() => setSelectedOption(option.key)}
                                          sx={{
                                            backgroundColor: selectedOption === option.key 
                                              ? 'rgba(102, 126, 234, 0.1)' 
                                              : 'white',
                                            borderColor: selectedOption === option.key 
                                              ? '#667eea !important' 
                                              : '#e2e8f0 !important',
                                            color: selectedOption === option.key 
                                              ? '#667eea' 
                                              : '#334155',
                                            boxShadow: selectedOption === option.key 
                                              ? '0 4px 8px rgba(102, 126, 234, 0.2)' 
                                              : 'none',
                                            '&:hover': {
                                              borderColor: '#667eea !important',
                                              backgroundColor: 'rgba(102, 126, 234, 0.05)',
                                            },
                                            // Special styling for referral when selected to accommodate the input
                                            ...(option.key === 'referral' && selectedOption === 'referral' && {
                                              flexDirection: 'column',
                                              alignItems: 'flex-start'
                                            })
                                          }}
                                        >
                                          {/* Main content row */}
                                          <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            width: '100%',
                                            justifyContent: 'space-between'
                                          }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                              <Typography sx={{ fontSize: '1.1rem' }}>
                                                {option.icon}
                                              </Typography>
                                              <Typography sx={{ fontWeight: 'inherit' }}>
                                                {option.label}
                                              </Typography>
                                            </Box>
                                            
                                            {selectedOption === option.key && (
                                              <CheckCircleIcon sx={{ color: '#667eea', fontSize: '1.2rem' }} />
                                            )}
                                          </Box>
                                          
                                          {/* Referral input - appears below the main content */}
                                          {option.key === 'referral' && selectedOption === 'referral' && (
                                            <Box sx={{ 
                                              width: '100%',
                                              mt: 2,
                                              pt: 2,
                                              borderTop: '1px solid #e2e8f0'
                                            }}>
                                              <TextField
                                                variant="outlined"
                                                size="small"
                                                placeholder="Who referred you?"
                                                value={referralDetail}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={(e) => setReferralDetail(e.target.value)}
                                                fullWidth
                                                sx={{ 
                                                  '& .MuiOutlinedInput-root': {
                                                    backgroundColor: 'white',
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                      borderColor: '#667eea',
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                      borderColor: '#667eea',
                                                    }
                                                  }
                                                }}
                                              />
                                            </Box>
                                          )}
                                        </Button>
                                      ))}
                                    </ButtonGroup>
                                    
                                    {!isOptionSelected && (
                                        <Typography 
                                          variant="caption" 
                                          className="error-msg options-msg"
                                          sx={{ 
                                            color: '#dc2626', 
                                            fontSize: '0.85rem',
                                            mt: 1,
                                            display: 'block'
                                          }}
                                        >
                                          Please choose one of the options
                                        </Typography>
                                    )}
                                </div>
                                
                                {/* Improved Other textarea */}
                                {selectedOption === 'other' && (
                                    <Box className='textBox' sx={{ mt: 2 }}>
                                        <TextField
                                            multiline
                                            rows={4}
                                            fullWidth
                                            label="Please describe how you heard about us"
                                            placeholder="Tell us more about how you discovered our service..."
                                            {...register('answer')}
                                            sx={{
                                              '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                  borderColor: '#667eea',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                  borderColor: '#667eea',
                                                }
                                              }
                                            }}
                                        />
                                    </Box>
                                )}
                                {errors.answer && (
                                    <Typography 
                                      variant="caption" 
                                      className="error-msg"
                                      sx={{ color: '#dc2626', fontSize: '0.85rem', mt: 1, display: 'block' }}
                                    >
                                      {errors.answer.message}
                                    </Typography>
                                )}
                            </div>
                        </div>
                        
                        {/* Improved Action Buttons */}
                        <div className="col-12">
                            <Box className="createStepBtn" sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              mt: 4,
                              gap: 2
                            }}>
                                <Button
                                    className="cancelBtn"
                                    variant="outlined"
                                    disabled={props?.userType?.user === "Customer"}
                                    onClick={() => props?.setActiveStep(4)}
                                    sx={{
                                      borderColor: '#e2e8f0',
                                      color: '#64748b',
                                      px: 3,
                                      py: 1.5,
                                      borderRadius: 2,
                                      textTransform: 'none',
                                      fontWeight: 500,
                                      '&:hover': {
                                        borderColor: '#cbd5e1',
                                        backgroundColor: '#f8fafc'
                                      }
                                    }}
                                >
                                    Previous
                                </Button>
                                <Button
                                    className="saveBtn"
                                    variant="contained"
                                    type="submit"
                                    disabled={!isOptionSelected || !!Object.keys(errors).length}
                                    endIcon={
                                    sendEmailLoading || updateCatererLoading ? (
                                        <CircularProgress color="inherit" size={16}/>
                                    ) : (
                                        <ArrowForwardIcon />
                                    )
                                    }
                                    sx={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    px: 3,
                                    py: 1.5,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    boxShadow: 'none',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
                                    },
                                    '&:disabled': {
                                        background: '#cbd5e1',
                                        color: '#94a3b8'
                                    }
                                    }}
                                >
                                    {sendEmailLoading || updateCatererLoading 
                                    ? "Sending..." 
                                    : "Send and Proceed"
                                    }
                                </Button>
                            </Box>
                            
                            {/* Improved Skip Button */}
                            <Box sx={{ textAlign: 'center', mt: 2 }}>
                                <Button 
                                  className="mt-2" 
                                  variant="text"
                                  endIcon={<ArrowForwardIcon className="ms-2"/>}
                                  onClick={() => skip()}
                                  sx={{
                                    color: "#64748b", 
                                    fontWeight: '600',
                                    textTransform: 'none',
                                    '&:hover': {
                                      backgroundColor: 'transparent',
                                      color: '#475569',
                                      textDecoration: 'underline'
                                    }
                                  }}
                                >
                                  Skip for now
                                </Button>
                            </Box>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}